import React, { Dispatch, SetStateAction, useMemo } from 'react';

import { Venue, type Product } from 'generated/graphql';
import { type ReducerAction, type Availability } from 'types';
import EditableText from 'components/Inputs/EditableText';

import { setDateToTomorrowAt4AM, useAvailabilityTypeByVariablesByApp, useErrorModal } from 'utils';
import { SelectMenu } from 'components/SelectMenu';
import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import { availabilities, AvailabilityStatus } from 'utils/constants';
import { useSetAvailability } from 'utils/Availability';
import { useModalContext } from 'common/Context/ModalContext';

import IconViewPosIds from 'icons/viewPosIds.svg';
import { ApolloError } from '@apollo/client';

import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { sanitizeUrl } from '@braintree/sanitize-url';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import styles from './DetailsHeader.module.scss';
import DetailsImage from './DetailsImage';
import { useItemContext } from './ItemContext';

const SNOOZE_FEATURE_FLAG = 'snoozeEnabled';

interface SelectMenuOption {
  content: JSX.Element;
  value: string;
  type: 'button';
  hidden: boolean;
}

const getImageUri = (item: Product | null, currentAppId: string | undefined): string | undefined => {
  if (!item) {
    return undefined;
  }

  // If DetailsHeader is rendered within All Items page, use the first the defined imageUri
  if (!currentAppId) {
    return item.variablesByApp.find((vba) => vba.imageUri)?.imageUri || undefined;
  }

  // If DetailsHeader is rendered within Single Menu page, use the imageUri of the current app
  return item.variablesByApp.find((vba) => vba.app.id === currentAppId)?.imageUri || undefined;
};

interface Props {
  item: Product | null;
  originalItem: Product | null;
  currentVenue: Venue | null | undefined;
  isUnpublished?: boolean;
  activeTab: string;
  closeModal: () => void;
  setActiveTab: (tab: string) => void;
  handleImageSubmit: (args: [imageFile: File, optimisticURL: string] | null) => void;
  handleChange: (args: ReducerAction) => void;
  hasScrolled: boolean;
  setIsAvailabilityLoading: Dispatch<SetStateAction<boolean>>;
  /**
   * - Used to determine which img src to use - Product.imageUri or Product.variablesByApp.imageUri,
   * - A truthy value means this component is being rendered via Single Menu page - get imageUri from Product.variablesByApp
   * - A falsy value means this component is being rendered via All Items page - get imageUri from Product
   */
  currentAppId: string | undefined;
}

const DetailsHeader: React.FC<Props> = ({
  item,
  originalItem,
  currentVenue,
  isUnpublished = false,
  closeModal,
  activeTab,
  setActiveTab,
  handleImageSubmit,
  handleChange,
  hasScrolled,
  setIsAvailabilityLoading,
  currentAppId
}) => {
  const { posProduct } = useItemContext();
  const { name } = item || {};
  const imageUri = getImageUri(item, currentAppId);
  const availabilityType = useAvailabilityTypeByVariablesByApp(item?.variablesByApp || []);
  const [handlePublishAvailability, availabilityChangeLoading] = useSetAvailability(originalItem, currentVenue, true);
  const errorModalHandler = useErrorModal();
  const [featureFlags, loading, error] = useFeatureFlagsQuery([SNOOZE_FEATURE_FLAG]);
  const isSnoozeEnabled = useMemo(() => {
    if (loading || error || !featureFlags) {
      return false;
    }
    const snoozeFeatureFlag = featureFlags[SNOOZE_FEATURE_FLAG];
    return !!snoozeFeatureFlag?.isEnabled;
  }, [featureFlags, loading, error]);

  const handleUnpublishedAvailabilityChange = (newAvailability: string): void => {
    const itemAvailability =
      newAvailability === AvailabilityStatus.UNAVAILABLE_TODAY ? AvailabilityStatus.UNAVAILABLE : newAvailability;
    const clonedVariablesByApps = item?.variablesByApp ? [...item.variablesByApp] : [];
    const newVariablesByApps = clonedVariablesByApps.map((varByApp) => ({
      ...varByApp,
      availability: itemAvailability,
      snoozedUntil:
        newAvailability === AvailabilityStatus.UNAVAILABLE_TODAY ? setDateToTomorrowAt4AM(currentVenue?.timezone) : null
    }));
    handleChange({ key: 'variablesByApp', payload: newVariablesByApps });
  };
  const availabilityName = availabilities.find((a) => a.slug === availabilityType)?.name || '';

  const handleAvailabilityChange = async (newAvailability: string): Promise<void> => {
    if (
      posProduct?.availability === AvailabilityStatus.UNAVAILABLE &&
      newAvailability !== AvailabilityStatus.UNAVAILABLE
    ) {
      errorModalHandler(
        new ApolloError({
          graphQLErrors: [
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            {
              extensions: {
                mmCode: 6002,
                requestId: undefined
              }
            }
          ]
        }),
        currentVenue
      );
      return;
    }
    if (!isUnpublished) {
      await handlePublishAvailability(newAvailability, setIsAvailabilityLoading, () =>
        handleUnpublishedAvailabilityChange(newAvailability)
      );
    } else handleUnpublishedAvailabilityChange(newAvailability);
  };
  const { openModal } = useModalContext();

  const tabs = [
    {
      label: 'General',
      value: 'general'
    },
    {
      label: 'Option Groups',
      value: 'optionGroups'
    }
  ];

  const onAvailabilityChangeClick = (value: string): void => {
    // eslint-disable-next-line no-restricted-globals
    const isInMenus = sanitizeUrl(location.pathname).includes('/menus/');
    logAmplitudeEvent({
      name: `set item availability`,
      category: isInMenus ? AmplitudeEventCategories.MM_SIDE_PANEL_MENU : AmplitudeEventCategories.MM_SIDE_PANEL_ITEMS,
      properties: {
        [AmplitudeEventProperties.ACTION]: `set ${
          value === AvailabilityStatus.UNAVAILABLE_TODAY ? 'unavailable until end of day' : value
        }`,
        [AmplitudeEventProperties.ITEM_POS_ID]: item?.posId || '',
        [AmplitudeEventProperties.ITEM_NAME]: item?.name || ''
      }
    });
    handleAvailabilityChange(value);
  };

  const selectMenu = (
    <SelectMenu
      options={availabilities.reduce<SelectMenuOption[]>((acc, availability: Availability) => {
        // Skip Unavailable Today when snooze is disabled
        if (!isSnoozeEnabled && availability.slug === AvailabilityStatus.UNAVAILABLE_TODAY) {
          return acc;
        }

        acc.push({
          content: (
            <>
              <i className={`Dot ${availability.slug}`} />
              <span>{availability.name}</span>
            </>
          ),
          value: availability.slug,
          type: 'button',
          hidden: availability.slug === AvailabilityStatus.MIXED && availabilityType !== AvailabilityStatus.MIXED
        });

        return acc;
      }, [])}
      defaultButtonContent={<AvailabilityPill loading={availabilityChangeLoading} availability={availabilityName} />}
      downIcon={null}
      onChange={onAvailabilityChangeClick}
      buttonStyle={styles.Price}
      align="right"
      size="small"
    />
  );

  return (
    <>
      <div className={`${styles.CornerContainer}`}>
        <div
          data-testid="corner-select-menu-outer"
          className={`${styles.SelectMenuOuter} ${hasScrolled ? styles.Visible : ''}`}
        >
          {selectMenu}
        </div>
        <SelectMenu
          aria-label="additional actions"
          options={[
            {
              content: (
                <span>
                  <img className={styles.Icon} alt="" src={IconViewPosIds} /> Show POS IDs
                </span>
              ),
              value: 'show-pos-ids',
              type: 'button',
              callback: () => {
                openModal({
                  type: 'POS_DEFAULTS',
                  menuItem: item
                });
              }
            }
          ]}
          defaultButtonContent={<i className="mm-ellipsis-v" />}
          buttonStyle={styles.MoreButton}
          downIcon={null}
          lineBeforeLast={false}
          align="right"
          size="small"
        />
        <button
          aria-label="details close button"
          data-testid="details-close-button"
          className={styles.CloseButton}
          type="button"
          onClick={closeModal}
        >
          <i className="mm-times" />
        </button>
      </div>
      <header data-testid="details-header" className={`${styles.Top} ${hasScrolled ? styles.Scrolled : ''}`}>
        <div className={styles.Intro}>
          <div className={styles.Image}>
            <DetailsImage name={name} src={imageUri} handleImageSubmit={handleImageSubmit} />
          </div>
          <div>
            <h3 data-testid="details-name">
              <EditableText
                text={name || ''}
                onSubmit={(newName: string) => {
                  handleChange({
                    key: 'name',
                    payload: newName
                  });
                }}
                testId="details-name-inner"
              />
            </h3>
            <div className={`${styles.SelectMenuOuter} ${!hasScrolled ? styles.Visible : ''}`}>{selectMenu}</div>
          </div>
        </div>
        <ul className={styles.Tabs}>
          {tabs.map(({ label, value }) => {
            return (
              <li key={label} className={activeTab === value ? styles.Active : ''}>
                <button
                  onClick={() => setActiveTab(value)}
                  id={`details-${value}-button`}
                  data-testid={`details-${value}-button`}
                  type="button"
                  tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      </header>
    </>
  );
};

export default DetailsHeader;
